import type { RoutedActionHandler } from "./actions.ts";
import type { DestinationRef } from "../features/navigation/destinations.ts";
import type { SidebarGroup } from "../domain/app-types.ts";

export interface ShellActionCapabilities {
  openSidebar(): void;
  closeSidebar(): void;
  toggleSidebarGroup(group: SidebarGroup): void | Promise<void>;
  toggleSpace(id: string): void | Promise<void>;
  toggleSidebarMenu(kind: "space" | "category", id: string, target: HTMLElement): void;
  closeDetail(): void | Promise<unknown>;
  viewTask(): void | Promise<void>;
  account(): void | Promise<void>;
  about(): void;
  theme(): void;
  notifications(): void | Promise<void>;
  invitations(): void | Promise<void>;
  externalCalendars(): void | Promise<void>;
  dataManagement(): void | Promise<void>;
  management(): void;
  appearance(): void;
  onboarding(): void;
  dismissOnboarding(): void | Promise<void>;
  toggleFavoriteDestination(destination: DestinationRef): void | Promise<void>;
  commandPalette(): void | Promise<void>;
  manualSync(): void | Promise<void>;
  syncStatus(): void | Promise<void>;
  quickNotes(): void | Promise<void>;
  planMyDay(): void | Promise<void>;
  weeklyReview(): void | Promise<void>;
  inboxTriage(): void | Promise<void>;
  priorityMatrix(): void | Promise<void>;
  resumeFocus(): void | Promise<void>;
  carryMyDay(): void | Promise<void>;
  leaveMyDay(id: string): void | Promise<void>;
  moveCalendar(direction: -1 | 0 | 1): void | Promise<void>;
  calendarFeed(): void | Promise<void>;
  newTemplate(): void | Promise<void>;
  manageCycles(): void | Promise<void>;
  closeCycle(): void | Promise<void>;
  saveTemplate(id: string): void | Promise<void>;
}

const names = new Set(["open-sidebar", "close-sidebar", "toggle-sidebar-group", "toggle-space", "toggle-sidebar-menu", "close-detail", "view-task", "account", "about", "theme", "notifications", "invitations", "external-calendars", "data-management", "management", "appearance", "onboarding", "dismiss-onboarding", "toggle-favorite-destination", "command-palette", "manual-sync", "sync-status", "quick-notes", "plan-my-day", "weekly-review", "inbox-triage", "priority-matrix", "resume-focus", "carry-my-day", "leave-my-day", "calendar-prev", "calendar-next", "calendar-today", "calendar-feed", "new-template", "manage-cycles", "close-cycle", "save-template"]);

export function createShellActionHandler(capabilities: ShellActionCapabilities): RoutedActionHandler {
  return async (name, target) => {
    if (!names.has(name)) return false;
    if (name === "open-sidebar") capabilities.openSidebar();
    if (name === "close-sidebar") capabilities.closeSidebar();
    if (name === "toggle-sidebar-group") await capabilities.toggleSidebarGroup(target.dataset.group as SidebarGroup);
    if (name === "toggle-space") await capabilities.toggleSpace(target.dataset.id || "");
    if (name === "toggle-sidebar-menu") capabilities.toggleSidebarMenu(target.dataset.kind as "space" | "category", target.dataset.id || "", target);
    if (name === "close-detail") await capabilities.closeDetail();
    if (name === "view-task") await capabilities.viewTask();
    if (name === "account") await capabilities.account();
    if (name === "about") capabilities.about();
    if (name === "theme") capabilities.theme();
    if (name === "notifications") await capabilities.notifications();
    if (name === "invitations") await capabilities.invitations();
    if (name === "external-calendars") await capabilities.externalCalendars();
    if (name === "data-management") await capabilities.dataManagement();
    if (name === "management") capabilities.management();
    if (name === "appearance") capabilities.appearance();
    if (name === "onboarding") capabilities.onboarding();
    if (name === "dismiss-onboarding") await capabilities.dismissOnboarding();
    if (name === "toggle-favorite-destination") await capabilities.toggleFavoriteDestination({ kind: target.dataset.destinationKind as DestinationRef["kind"], id: target.dataset.id || "", rootId: target.dataset.rootId });
    if (name === "command-palette") await capabilities.commandPalette();
    if (name === "manual-sync") await capabilities.manualSync();
    if (name === "sync-status") await capabilities.syncStatus();
    if (name === "quick-notes") await capabilities.quickNotes();
    if (name === "plan-my-day") await capabilities.planMyDay();
    if (name === "weekly-review") await capabilities.weeklyReview();
    if (name === "inbox-triage") await capabilities.inboxTriage();
    if (name === "priority-matrix") await capabilities.priorityMatrix();
    if (name === "resume-focus") await capabilities.resumeFocus();
    if (name === "carry-my-day") await capabilities.carryMyDay();
    if (name === "leave-my-day") await capabilities.leaveMyDay(target.dataset.id || "");
    if (name === "calendar-prev") await capabilities.moveCalendar(-1);
    if (name === "calendar-next") await capabilities.moveCalendar(1);
    if (name === "calendar-today") await capabilities.moveCalendar(0);
    if (name === "calendar-feed") await capabilities.calendarFeed();
    if (name === "new-template") await capabilities.newTemplate();
    if (name === "manage-cycles") await capabilities.manageCycles();
    if (name === "close-cycle") await capabilities.closeCycle();
    if (name === "save-template") await capabilities.saveTemplate(target.dataset.id || "");
    return true;
  };
}
